import { useMemo } from "react";
import { Button, Modal } from "@legal-500/legal500-design-system";
import type { BackendMissingTranslation } from "./surveyDetails.types";
import type { SurveyDefinition, SurveyDefinitionElement } from "./surveyDetails.types";
import styles from "./SurveyDetails.module.css";

type MissingTranslationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onGoToTranslations: () => void;
  missing: BackendMissingTranslation[];
  definition: SurveyDefinition;
  languageLabels: Record<string, string>;
};

type GroupedEntry = {
  field: BackendMissingTranslation["field"];
  label: string;
  missingLocales: string[];
};

type MissingGroup = {
  key: string;
  questionLabel: string;
  entries: GroupedEntry[];
};

const PAGE_INDEX_REGEX = /pages\[(\d+)\]/;
const ELEMENT_INDEX_REGEX = /elements\[(\d+)\]/;
const CHOICE_INDEX_REGEX = /choices\[(\d+)\]/;
const RANGE_LABEL_INDEX_REGEX = /rangeLabels\[(\d+)\]/;

function extractIndex(path: string, regex: RegExp): number | null {
  const match = path.match(regex);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

function getElementFromPath(
  definition: SurveyDefinition,
  path: string,
): {
  element: SurveyDefinitionElement | null;
  pageIndex: number | null;
  elementIndex: number | null;
} {
  const pageIndex = extractIndex(path, PAGE_INDEX_REGEX);
  const elementIndex = extractIndex(path, ELEMENT_INDEX_REGEX);
  if (pageIndex === null || elementIndex === null) {
    return { element: null, pageIndex: null, elementIndex: null };
  }
  const page = definition.pages[pageIndex];
  if (!page) return { element: null, pageIndex, elementIndex };
  const element = page.elements[elementIndex] ?? null;
  return { element, pageIndex, elementIndex };
}

function pickStringFromMaybeLocalized(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value;
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.default === "string" && record.default.trim()) return record.default;
  }
  return undefined;
}

function getQuestionLabel(
  element: SurveyDefinitionElement | null,
  pageIndex: number | null,
  elementIndex: number | null,
): string {
  if (!element) {
    if (pageIndex !== null && elementIndex !== null) {
      return `Question ${pageIndex + 1}.${elementIndex + 1}`;
    }
    return "Question";
  }
  return (
    pickStringFromMaybeLocalized(element.title) ??
    (typeof element.name === "string" ? element.name : undefined) ??
    `Question ${(elementIndex ?? 0) + 1}`
  );
}

function getChoiceLabel(element: SurveyDefinitionElement | null, path: string): string {
  const choiceIndex = extractIndex(path, CHOICE_INDEX_REGEX);
  if (!element || choiceIndex === null) {
    return "Choice";
  }
  const choices = element.choices as unknown as Array<unknown> | undefined;
  if (!Array.isArray(choices)) return `Choice ${choiceIndex + 1}`;
  const choice = choices[choiceIndex];
  if (choice && typeof choice === "object") {
    const record = choice as Record<string, unknown>;
    const text = pickStringFromMaybeLocalized(record.text);
    if (text) return text;
    if (typeof record.value === "string" && record.value.trim()) return record.value;
  }
  if (typeof choice === "string" && choice.trim()) return choice;
  return `Choice ${choiceIndex + 1}`;
}

function getRangeScaleLabelCaption(element: SurveyDefinitionElement | null, path: string): string {
  const labelIndex = extractIndex(path, RANGE_LABEL_INDEX_REGEX);
  if (!element || labelIndex === null) {
    return "Scale label";
  }
  const rangeLabels = element.rangeLabels;
  if (!Array.isArray(rangeLabels)) return `Scale label ${labelIndex + 1}`;
  const row = rangeLabels[labelIndex] as Record<string, unknown> | undefined;
  if (!row || typeof row !== "object") return `Scale label ${labelIndex + 1}`;
  const caption = pickStringFromMaybeLocalized(row.label);
  return caption ?? `Scale label ${labelIndex + 1}`;
}

function fieldLabel(
  field: BackendMissingTranslation["field"],
  element: SurveyDefinitionElement | null,
  path: string,
): string {
  if (path.includes("rangeLabels[")) {
    return `Scale label "${getRangeScaleLabelCaption(element, path)}"`;
  }
  if (field === "title") return "Title";
  if (field === "description") return "Description";
  if (field === "subjectLabel") return "Subject label";
  if (field === "noExperienceLabel") return '"No experience" label';
  if (field === "html") {
    if (element?.name === "footer") return "Footer";
    if (element?.name === "survey_header") return "Header";
    return "Content";
  }
  return `Choice "${getChoiceLabel(element, path)}"`;
}

function formatLocales(locales: string[], languageLabels: Record<string, string>): string {
  return locales.map(code => languageLabels[code] ?? code.toUpperCase()).join(", ");
}

function MissingTranslationsModal({
  isOpen,
  onClose,
  onGoToTranslations,
  missing,
  definition,
  languageLabels,
}: MissingTranslationsModalProps) {
  const groups = useMemo<MissingGroup[]>(() => {
    const map = new Map<string, MissingGroup>();
    for (const entry of missing) {
      const { element, pageIndex, elementIndex } = getElementFromPath(definition, entry.path);
      const groupKey =
        pageIndex !== null && elementIndex !== null
          ? `pages[${pageIndex}].elements[${elementIndex}]`
          : entry.path;
      const questionLabel = getQuestionLabel(element, pageIndex, elementIndex);
      let group = map.get(groupKey);
      if (!group) {
        group = { key: groupKey, questionLabel, entries: [] };
        map.set(groupKey, group);
      }
      group.entries.push({
        field: entry.field,
        label: fieldLabel(entry.field, element, entry.path),
        missingLocales: entry.missingLocales,
      });
    }
    return Array.from(map.values());
  }, [missing, definition]);

  const handleGoToTranslations = () => {
    onGoToTranslations();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Missing translations"
      size="medium"
      backdropClick={false}
    >
      <div className={styles.missingTranslationsModal}>
        <p className={styles.missingTranslationsIntro}>
          This survey cannot be published yet. The following items are missing translations:
        </p>
        <ul className={styles.missingTranslationsList}>
          {groups.map(group => (
            <li key={group.key} className={styles.missingTranslationsItem}>
              <span className={styles.missingTranslationsQuestion}>{group.questionLabel}</span>
              <ul className={styles.missingTranslationsFields}>
                {group.entries.map((entry, idx) => (
                  <li key={`${group.key}-${entry.label}-${idx}`}>
                    <span className={styles.missingTranslationsField}>{entry.label}:</span>{" "}
                    <span>{formatLocales(entry.missingLocales, languageLabels)}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <div className={styles.modalActions}>
          <Button variant="outlined" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" size="small" onClick={handleGoToTranslations}>
            Go to Translations
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export { MissingTranslationsModal };
